import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { CompanyProvider } from "../../context/CompanyContext";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import { BreadcrumbProvider } from "../../context/BreadcrumbContext";
import CommonSidebar from "./CommonSidebar";
import "../css/Dashboard.css";

/**
 * CommonDashboardLayout - Reusable layout for all dashboard roles
 * Includes CompanyProvider and BreadcrumbProvider internally
 * 
 * Props:
 * - menuItems: array - Menu items configuration for sidebar
 * - roleType: 'admin' | 'seller' | 'projectManager' | 'buyer' (for tracking)
 */
export default function CommonDashboardLayout({
  menuItems,
  roleType = "admin"
}) {
  return (
    <CompanyProvider>
      <BreadcrumbProvider>
        <CommonDashboardLayoutContent menuItems={menuItems} roleType={roleType} />
      </BreadcrumbProvider>
    </CompanyProvider>
  );
}

function CommonDashboardLayoutContent({
  menuItems,
  roleType = "admin"
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const location = useLocation();
  const { fetchCompanyDetails } = useCompany();

  // Fetch company details only once on mount (skip for admin users)
  useEffect(() => {
    if (roleType !== 'admin') {
      fetchCompanyDetails();
    }
  }, [fetchCompanyDetails, roleType]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setCollapsed(mobile);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setCollapsed((prev) => !prev);

  return (
    <div className="dashboard-layout">
      <CommonSidebar
        collapsed={collapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        menuItems={menuItems}
        role={roleType}
      />
      <div className={`main-content-area ${collapsed ? "collapsed" : ""}`}>
        <div
          className="main-header d-flex justify-content-between align-items-center px-3 py-2 shadow-sm flex-wrap gap-2"
          style={{
            backgroundColor: 'white',
            position: 'fixed',
            top: 0,
            left: isMobile ? 0 : (collapsed ? 70 : 260),
            width: isMobile ? '100%' : `calc(100% - ${collapsed ? 70 : 260}px)`,
            right: 0,
            zIndex: 1050
          }}
        >
          <CommonDashboardHeader
            isMobile={isMobile}
            collapsed={collapsed}
            toggleSidebar={toggleSidebar}
            location={location}
          />
        </div>
        <div className="main-content px-4 mt-5 py-2">
          <Outlet />
        </div>
      </div>
      {!collapsed && isMobile && (
        <div
          onClick={toggleSidebar}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.4)",
            zIndex: 998,
          }}
        />
      )}
    </div>
  );
}

/**
 * CommonDashboardHeader - Reusable header for all dashboards
 * Handles breadcrumb logic based on route
 */
export function CommonDashboardHeader({
  isMobile,
  collapsed,
  toggleSidebar,
  location,
  useBreadcrumbContext = true
}) {
  const { logo } = useCompany();
  const { communicationsTitle } = useBreadcrumb();

  const getBreadcrumb = () => {
    const path = location.pathname;
    
    // Admin dashboard breadcrumbs
    if (path.includes("/admin-dashboard")) {
      if (path.includes("/buyers")) return "Registration > Buyers";
      if (path.includes("/sellers")) return "Registration > Sellers";
      if (path.includes("/project-manager")) return "Registration > Project Manager";
      if (path.includes("/contracts")) return "Contracts";
      if (path.includes("/rfqs")) return "Rfqs";
      if (path.includes("/projects")) return "Projects";
    }
    
    // Seller dashboard breadcrumbs
    if (path.includes("/seller-dashboard")) {
      if (path.includes("/projects")) return "Projects";
      if (path.includes("/requirements")) return "Requirements";
    }
    
    // Project Manager dashboard breadcrumbs
    if (path.includes("/project-manager-dashboard")) {
      if (path.includes("/projects")) return "Projects";
      if (path.includes("/settings")) return "Settings";
    }
    
    // Buyer dashboard breadcrumbs
    if (path.includes("/buyer-dashboard")) {
      if (path.includes("/projects")) return "Projects";
      if (path.includes("/requirements")) return "Requirements";
      if (path.includes("/users")) return "Users";
      if (path.includes("/settings")) return "Settings";
      if (path.includes("/review-sellers")) return "Review Sellers";
    }

    // Communication breadcrumb (common for all)
    if (path.includes("/communication")) {
      return communicationsTitle 
        ? `Communications - ${communicationsTitle}` 
        : "Communications";
    }

    return "Dashboard";
  };

  return (
    <>
      <div className="d-flex align-items-center flex-shrink-1">
        {isMobile && (
          <button
            className="btn btn-sm p-0 border-0 bg-transparent"
            onClick={toggleSidebar}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        <div
          className="breadcrumb-text ms-2 d-flex align-items-center gap-2 flex-nowrap"
          style={{ minWidth: 0 }}
        >
          <img src="/images/home.svg" alt="home" />
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: "calc(100vw - 150px)" }}
          >
            {getBreadcrumb()}
          </span>
        </div>
      </div>

      <div className="header-right d-flex align-items-center gap-3 flex-shrink-0">
        {/* <img
          src="/images/search.svg"
          alt="search"
          style={{
            width: isMobile ? "18px" : "24px",
            height: isMobile ? "18px" : "24px",
          }}
        /> */}
        {/* <img
          src="/images/notification.svg"
          alt="notif"
          style={{
            width: isMobile ? "18px" : "24px",
            height: isMobile ? "18px" : "24px",
          }}
        /> */}
        <img
          src={logo || "/images/profile.svg" }
          alt="company logo"
          style={{
            width: isMobile ? "22px" : "28px",
            height: isMobile ? "22px" : "28px",
            borderRadius: "4px",
            objectFit: "cover"
          }}
        />
      </div>
    </>
  );
}
